import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { FiCalendar, FiCheck, FiClock, FiGlobe, FiVideo, FiArrowLeft } from 'react-icons/fi';
import api from '../config/api';
import BrandLogo from '../components/BrandLogo';
import ThemeToggle from '../components/ThemeToggle';
import LoadingButton from '../components/LoadingButton';
import './PublicBookingPage.css';

function toIsoDate(d) {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
}

function formatSlotDay(iso) {
  try {
    return new Date(iso).toLocaleDateString([], {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatSlotTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatSlotFull(iso) {
  try {
    return new Date(iso).toLocaleString([], {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function initialsFromName(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function dayKey(iso) {
  return String(iso || '').slice(0, 10);
}

/**
 * Public shareable booking page — /book/:slug (no app chrome).
 */
export default function PublicBookingPage() {
  const { slug } = useParams();
  const [dayFrom, setDayFrom] = useState(() => toIsoDate(new Date()));
  const [slot, setSlot] = useState(null);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [done, setDone] = useState(null);
  const [error, setError] = useState('');
  /** On narrow screens: times → details */
  const [step, setStep] = useState('times');

  const dayTo = useMemo(() => {
    const d = new Date(`${dayFrom}T00:00:00`);
    d.setDate(d.getDate() + 14);
    return toIsoDate(d);
  }, [dayFrom]);

  const { data: page, isLoading: loadingPage, error: pageError } = useQuery(
    ['public-booking', slug],
    () => api.get(`/public/booking/${encodeURIComponent(slug)}`).then((r) => r.data),
    { enabled: Boolean(slug), retry: false }
  );

  const { data: slotsData, isLoading: loadingSlots } = useQuery(
    ['public-slots', slug, dayFrom, dayTo],
    () =>
      api
        .get(`/public/booking/${encodeURIComponent(slug)}/slots`, {
          params: { from: dayFrom, to: dayTo },
        })
        .then((r) => r.data),
    { enabled: Boolean(slug) && Boolean(page), staleTime: 15_000 }
  );

  const slots = useMemo(() => {
    const list = slotsData?.slots || slotsData || [];
    return Array.isArray(list) ? list : [];
  }, [slotsData]);

  const slotsByDay = useMemo(() => {
    const map = new Map();
    slots.forEach((s) => {
      const key = dayKey(s.starts_at);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    });
    return Array.from(map.entries());
  }, [slots]);

  const bookMutation = useMutation(
    () =>
      api.post(`/public/booking/${encodeURIComponent(slug)}/book`, {
        guest_name: guestName.trim(),
        guest_email: guestEmail.trim(),
        starts_at: slot.starts_at,
        ends_at: slot.ends_at,
        notes: notes || undefined,
      }),
    {
      onSuccess: (res) => setDone(res.data),
      onError: (e) => setError(e.response?.data?.error || e.message),
    }
  );

  const consultantName = page?.consultant_name || null;
  const duration = page?.slot_minutes || 30;
  const timezone = page?.timezone || 'UTC';
  const unavailable = Boolean(pageError || (!loadingPage && !page));

  const selectSlot = (s) => {
    setSlot(s);
    setError('');
    setStep('details');
  };

  return (
    <div className="pub-book">
      <div className="pub-book-atmosphere" aria-hidden="true">
        <span className="pub-book-orb pub-book-orb--a" />
        <span className="pub-book-orb pub-book-orb--b" />
        <span className="pub-book-grid" />
      </div>

      <header className="pub-book-topbar">
        <BrandLogo variant="auth" />
        <ThemeToggle />
      </header>

      <main className="pub-book-main">
        {loadingPage && (
          <div className="pub-book-panel pub-book-panel--solo" aria-busy="true">
            <p className="pub-book-muted">Loading booking page…</p>
          </div>
        )}

        {unavailable && (
          <div className="pub-book-panel pub-book-panel--solo" role="alert">
            <div className="pub-book-empty-icon" aria-hidden>
              <FiCalendar />
            </div>
            <h1 className="pub-book-heading">Link unavailable</h1>
            <p className="pub-book-muted">
              This booking page is inactive or the link is invalid. Ask your consultant for a new
              invite.
            </p>
          </div>
        )}

        {page && done && (
          <div className="pub-book-panel pub-book-panel--solo pub-book-success">
            <div className="pub-book-success-mark" aria-hidden>
              <FiCheck />
            </div>
            <h1 className="pub-book-heading">You&apos;re booked</h1>
            <p className="pub-book-success-when">
              {formatSlotFull(done.starts_at || slot?.starts_at)}
            </p>
            <p className="pub-book-muted">
              A calendar invite{done.meeting_url ? ' with Google Meet' : ''} will go to{' '}
              <strong>{guestEmail}</strong>
              {consultantName ? (
                <>
                  {' '}
                  · with <strong>{consultantName}</strong>
                </>
              ) : null}
              .
            </p>
            {done.meeting_url ? (
              <a
                className="pub-book-meet-link"
                href={done.meeting_url}
                target="_blank"
                rel="noreferrer"
              >
                <FiVideo /> Join with Google Meet
              </a>
            ) : null}
          </div>
        )}

        {page && !done && (
          <div className="pub-book-shell">
            <aside className="pub-book-aside">
              <div className="pub-book-host">
                <span className="pub-book-avatar" aria-hidden>
                  {initialsFromName(consultantName || page.title)}
                </span>
                <div>
                  {consultantName ? (
                    <p className="pub-book-host-name">{consultantName}</p>
                  ) : null}
                  <h1 className="pub-book-title">{page.title || 'Book an appointment'}</h1>
                </div>
              </div>
              {page.description ? (
                <p className="pub-book-desc">{page.description}</p>
              ) : (
                <p className="pub-book-desc pub-book-desc--soft">
                  Choose a time that works for you. You&apos;ll get a calendar invite after
                  confirming.
                </p>
              )}
              <ul className="pub-book-meta">
                <li>
                  <FiClock aria-hidden /> {duration} minutes
                </li>
                <li>
                  <FiVideo aria-hidden /> Google Meet
                </li>
                <li>
                  <FiGlobe aria-hidden /> {timezone}
                </li>
              </ul>
              {slot ? (
                <div className="pub-book-selected">
                  <span className="pub-book-selected-label">Selected</span>
                  <strong>{formatSlotFull(slot.starts_at)}</strong>
                  <button
                    type="button"
                    className="pub-book-change"
                    onClick={() => {
                      setSlot(null);
                      setStep('times');
                    }}
                  >
                    Change time
                  </button>
                </div>
              ) : null}
            </aside>

            <section
              className={`pub-book-panel pub-book-flow ${
                step === 'details' && slot ? 'is-details' : 'is-times'
              }`}
              aria-label="Book a time"
            >
              {step === 'details' && slot ? (
                <button
                  type="button"
                  className="pub-book-back"
                  onClick={() => setStep('times')}
                >
                  <FiArrowLeft /> Back to times
                </button>
              ) : null}

              <div className="pub-book-step pub-book-step--times">
                <header className="pub-book-step-head">
                  <h2>Select a time</h2>
                  <label className="pub-book-date-field">
                    <span>From</span>
                    <input
                      type="date"
                      value={dayFrom}
                      min={toIsoDate(new Date())}
                      onChange={(e) => {
                        setDayFrom(e.target.value);
                        setSlot(null);
                        setStep('times');
                      }}
                    />
                  </label>
                </header>

                {loadingSlots ? (
                  <p className="pub-book-muted">Loading availability…</p>
                ) : slots.length === 0 ? (
                  <div className="pub-book-empty">
                    <FiCalendar aria-hidden />
                    <p>
                      No open slots in the next two weeks from this date. Try another start date, or
                      check back after the consultant publishes hours.
                    </p>
                  </div>
                ) : (
                  <div className="pub-book-days">
                    {slotsByDay.map(([key, daySlots]) => (
                      <div key={key} className="pub-book-day">
                        <h3 className="pub-book-day-label">{formatSlotDay(daySlots[0].starts_at)}</h3>
                        <div className="pub-book-slots">
                          {daySlots.map((s) => {
                            const active = slot?.starts_at === s.starts_at;
                            return (
                              <button
                                key={s.starts_at}
                                type="button"
                                className={`pub-book-slot${active ? ' is-active' : ''}`}
                                aria-pressed={active}
                                onClick={() => selectSlot(s)}
                              >
                                {formatSlotTime(s.starts_at)}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="pub-book-step pub-book-step--details">
                <header className="pub-book-step-head">
                  <h2>Your details</h2>
                  {slot ? (
                    <p className="pub-book-muted pub-book-slot-summary">
                      {formatSlotFull(slot.starts_at)} · {duration} min
                    </p>
                  ) : (
                    <p className="pub-book-muted">Pick a time first</p>
                  )}
                </header>

                {error ? (
                  <p className="pub-book-error" role="alert">
                    {error}
                  </p>
                ) : null}

                <div className="pub-book-form">
                  <label className="pub-book-field">
                    <span>Name</span>
                    <input
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      autoComplete="name"
                      required
                      placeholder="Your full name"
                    />
                  </label>
                  <label className="pub-book-field">
                    <span>Email</span>
                    <input
                      type="email"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      autoComplete="email"
                      required
                      placeholder="you@example.com"
                    />
                  </label>
                  <label className="pub-book-field">
                    <span>Notes (optional)</span>
                    <textarea
                      rows={3}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Anything helpful to prepare for the meeting"
                    />
                  </label>
                  <LoadingButton
                    type="button"
                    className="btn btn-primary pub-book-submit"
                    loading={bookMutation.isLoading}
                    disabled={!slot || !guestName.trim() || !guestEmail.trim()}
                    onClick={() => {
                      setError('');
                      bookMutation.mutate();
                    }}
                  >
                    <FiCheck /> Confirm booking
                  </LoadingButton>
                </div>
              </div>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
